import React, { useState, useCallback } from "react";
import { Tag, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
interface HeaderSectionProps {
  selectedCategoryName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedProductType?: any;
  subtitle?: string;
  description?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseVoucher = (voucher: any) => {
  if (!voucher || typeof voucher !== "object") return null;
  const code = voucher.code?.toString().trim() || "";
  const rawVal = voucher.discount_value ?? voucher.discount_amount ?? voucher.value;
  const val = rawVal !== undefined && rawVal !== null ? parseFloat(rawVal.toString()) : null;
  const type = voucher.discount_value_type || voucher.discount_type;
  const maxDiscount = voucher.max_discount_value || voucher.max_discount;
  let headline = "";
  if (val !== null && !isNaN(val)) {
    if (type === "percentage") {
      headline = `${val}% OFF`;
      if (maxDiscount && parseFloat(maxDiscount.toString()) > 0) {
        headline += ` UPTO ₹${parseFloat(maxDiscount.toString())}`;
      }
    } else if (type === "fixed" || type === "flat" || type === "amount") {
      headline = `FLAT ₹${val} OFF`;
    } else {
      headline = `${val} OFF`;
    }
  }
  if (!headline) return null;
  return {
    headline,
    code
  };
};
const HeaderSection: React.FC<HeaderSectionProps> = ({
  selectedCategoryName,
  selectedProductType
}) => {
  const [copied, setCopied] = useState(false);
  const title = selectedProductType?.label || selectedProductType?.name || selectedProductType?.title || selectedCategoryName || selectedProductType?.metadata?.meta_heading || "All Products";
  const displayDescription = selectedProductType?.metadata?.description || selectedProductType?.metadata?.content || selectedProductType?.metadata?.meta_description || "A thoughtfully assembled collection of treasures for the modern muse. From hand-poured essences to bespoke artistry.";

  // Get first applicable voucher
  const vouchers = selectedProductType?.applicable_vouchers;
  const firstVoucher = Array.isArray(vouchers) && vouchers.length > 0 ? parseVoucher(vouchers[0]) : null;
  const handleCopy = useCallback(() => {
    if (!firstVoucher?.code) return;
    navigator.clipboard.writeText(firstVoucher.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [firstVoucher?.code]);
  return <div className="relative w-full overflow-hidden bg-[#FDF6F0] py-12 md:py-16 px-6 mb-4 lg:mb-8">
      {/* Background Elements */}
      <div className="absolute top-[10%] right-[-5%] w-[200px] h-[200px] md:w-[300px] md:h-[300px] rounded-full bg-v2-coral/20 blur-3xl pointer-events-none animate-pulse" style={{
      animationDelay: "-6s"
    }}></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[250px] h-[250px] rounded-full bg-v2-tertiary/20 blur-3xl pointer-events-none animate-pulse" style={{
      animationDelay: "-2s"
    }}></div>
      <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none" style={{
      backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
    }}></div>

      <div className="max-w-4xl mx-auto relative z-10 flex flex-col items-center text-center">
        <div className="relative inline-block mb-6 ">
          <div className="absolute -top-8 -left-10 text-v2-tertiary opacity-60 hidden lg:block">
            <svg fill="currentColor" height="32" viewBox="0 0 24 24" width="32">
              <path d="M12,2L14.4,8.6L21,11L14.4,13.4L12,20L9.6,13.4L3,11L9.6,8.6L12,2Z"></path>
            </svg>
          </div>
          <div className="absolute -bottom-2 -right-12 text-v2-coral opacity-50 hidden lg:block">
            <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
              <path d="M12,2L14.4,8.6L21,11L14.4,13.4L12,20L9.6,13.4L3,11L9.6,8.6L12,2Z"></path>
            </svg>
          </div>
          <h1 className="text-4xl md:text-[55px] font-serif font-semibold text-v2-primary tracking-tight leading-none mb-3">
            {title}
          </h1>
          <div className="h-1 w-20 bg-gradient-to-r from-v2-tertiary via-v2-coral to-v2-primary mx-auto rounded-full mt-1 "></div>
        </div>
        <p className="text-xs md:text-base text-v2-on-surface-variant/60 font-serif leading-relaxed max-w-2xl">
          {displayDescription}
        </p>

        {/* Voucher Badge */}
        {firstVoucher && <Button onClick={handleCopy} variant="outline" className="mt-5 group inline-flex items-center gap-2.5 bg-white/70 backdrop-blur-sm border-[#037A6A]/20 rounded-full px-5 py-5 shadow-[0_2px_12px_rgba(3,122,106,0.08)] hover:shadow-[0_4px_20px_rgba(3,122,106,0.15)] hover:border-[#037A6A]/40 transition-all duration-300 cursor-pointer">
            <Tag className="w-4 h-4 text-[#037A6A] -rotate-45 flex-shrink-0" strokeWidth={2.5} />
            <span className="font-extrabold text-sm tracking-tight text-[#1E1E1E]">
              {firstVoucher.headline}
            </span>
            {firstVoucher.code && <>
                <span className="w-px h-4 bg-gray-300" />
                <span className="text-xs font-semibold text-[#037A6A] tracking-wide flex items-center gap-1">
                  {copied ? <>
                      <Check className="w-3.5 h-3.5" /> Copied!
                    </> : <>
                      <Copy className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                      {firstVoucher.code}
                    </>}
                </span>
              </>}
          </Button>}
      </div>
    </div>;
};
export default HeaderSection;